// =============================================================================
// PRICING SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React from 'react';
import { Check, Star } from 'lucide-react';
import { Button } from '@/components/Button';
import { createActionHandler, convertButtonToAction } from '@/utils/actionHandler';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { PricingComponent } from '@/types/components';

interface PricingSectionProps {
  component: ContentComponent;
}

/**
 * Type guard for new PricingComponent format
 */
function isNewPricingFormat(component: ContentComponent): boolean {
  return (
    component.type === 'pricing' &&
    'items' in component &&
    Array.isArray((component as any).items)
  );
}

/**
 * Pricing Section Component
 */
export const PricingSection: React.FC<PricingSectionProps> = ({ component }) => {
  if (isNewPricingFormat(component)) {
    return <PricingSectionNew component={component as unknown as any} />;
  }
  return <PricingSectionLegacy component={component} />;
};

/**
 * New format Pricing Section
 */
const PricingSectionNew: React.FC<{ component: PricingComponent }> = ({ component }) => {
  const { id, content, items, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  return (
    <section id={id} className={`content-section pricing-section ${layoutClasses}`} style={displayStyles}>
      <div className="container">
        {content.title && (
          <div className="section-header">
            <h2>{content.title}</h2>
            {content.description && <p>{content.description}</p>}
          </div>
        )}
        
        <div className={`pricing-grid grid-cols-${layout.columns || 3}`}>
          {items.map((tier, index) => (
            <div key={tier.id || index} className={`pricing-card ${tier.popular ? 'popular' : ''}`}>
              {tier.popular && (
                <div className="pricing-badge">
                  <Star size={16} />
                  <span>Most Popular</span>
                </div>
              )}
              
              <div className="pricing-header">
                <h3 className="pricing-name">{tier.name}</h3>
                <div className="pricing-price">
                  <span className="price-amount">{tier.price}</span>
                  {tier.period && <span className="price-period">/{tier.period}</span>}
                </div>
                {tier.description && (
                  <p className="pricing-description">{tier.description}</p>
                )}
              </div>
              
              <div className="pricing-features">
                <ul className="features-list">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="feature-item">
                      <Check size={16} className="feature-check" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {tier.action && (
                <div className="pricing-actions">
                  <Button
                    variant={tier.action.variant || 'primary'}
                    size={tier.action.size || 'lg'}
                    onClick={createActionHandler(tier.action)}
                    disabled={tier.action.disabled}
                  >
                    {tier.action.label}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Legacy format Pricing Section
 */
const PricingSectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const pricing = metadata?.pricing || [];
  const columns = metadata?.columns || 3;
  
  return (
    <section id={id} className="content-section pricing-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        <div className={`pricing-grid grid-cols-${columns}`}>
          {pricing.map((tier: any, index: number) => {
            const action = tier.button ? convertButtonToAction(tier.button) : null;
            
            return (
              <div key={index} className={`pricing-card ${tier.popular ? 'popular' : ''}`}>
                {tier.popular && (
                  <div className="pricing-badge">
                    <Star size={16} />
                    <span>Most Popular</span>
                  </div>
                )}
                
                <div className="pricing-header">
                  <h3 className="pricing-name">{tier.name}</h3>
                  <div className="pricing-price">
                    <span className="price-amount">{tier.price}</span>
                    {tier.period && <span className="price-period">/{tier.period}</span>}
                  </div>
                  {tier.description && (
                    <p className="pricing-description">{tier.description}</p>
                  )}
                </div>
                
                <div className="pricing-features">
                  <ul className="features-list">
                    {tier.features.map((feature: string, featureIndex: number) => (
                      <li key={featureIndex} className="feature-item">
                        <Check size={16} className="feature-check" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {action && (
                  <div className="pricing-actions">
                    <Button
                      variant={action.variant || 'primary'}
                      size={action.size || 'lg'}
                      onClick={createActionHandler(action)}
                    >
                      {action.label}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export { PricingSectionNew, PricingSectionLegacy };

